import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal
} from '@angular/core';
import { DialogWrapperComponent } from '../../wrappers/dialog-wrapper/dialog-wrapper.component';
import { DatePickerComponent } from '../../controllers/date-picker/date-picker.component';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { TimePickerComponent } from '../../controllers/time-picker/time-picker.component';
import { ButtonComponent } from '../../button/button.component';
import { ButtonSize, ButtonStyle } from '../../../enums/button.enum';
import {
  FormattedHours,
  getWorkingHoursForDate
} from '../../../utils/business-hours.util';
import { DIALOG_DATA } from '@angular/cdk/dialog';
import {
  DeliveryMethodSelectedAddress,
  ShippingMethodComponent
} from '../../shipping-method/shipping-method.component';
import { Store } from '@ngxs/store';
import { RestaurantState } from '../../../state/restaurant/restaurant.state';
import {
  WooCommerceDatetimeDeliveryDate,
  WooCommerceDatetimeTimeSlot,
  WooCommerceDeliveryAddress,
  WooCommerceShippingAddress
} from '../../../../core/interfaces/woo-commerce/api/woo-commerce-api.interface';
import { TimeValidators } from '../../../validators/time.validators';
import { convertToDate, convertToString } from '../../../utils/date.utils';
import { filterOutHolidays } from '../../../utils/holidays.utils';
import { DynamicValidationDependencies } from '../../controllers/control-error/validation-error-messages.token';
import {
  WooCommerceRestaurantHolidays,
  WooCommerceRestaurantHoursValue
} from '../../../../core/interfaces/woo-commerce/api/restaurant/restaurant-api.interface';
import { ShippingMethod } from '../../../../core/enums/woo-commerce/shipping/shipping.enum';
import { DeliveryState } from '../../../state/delivery/delivery.state';
import { AddDeliveryAddress } from '../../../state/delivery/delivery.actions';
import { BaseDialogDirective } from '../../base/base-dialog.directive';
import { CheckboxComponent } from '../../controllers/checkbox/checkbox.component';
import { WooCommerceDatetimeTimeSlotValue } from '../../../../core/enums/woo-commerce/api/woo-commerce-api.enum';
import { WooCommerceShippingDatetimeApiService } from '../../../../core/services/woo-commerce/api/woo-commerce-shipping-datetime-api.service';
import { WooCommercePickupLocationDatetimeResponse } from '../../../../core/interfaces/woo-commerce/api/shipping-datetime/shipping-datetime-api.interface';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize } from 'rxjs';
import { WooCommerceShippingApiService } from '../../../../core/services/woo-commerce/api/woo-commerce-shipping-api.service';
import { findNotEmptyField } from '../../../utils/array.util';
import { parseTimeRange } from '../../../utils/time.util';
import { ToastrService } from 'ngx-toastr';
import { filterDatesByWeekdayAvailability } from '../../../utils/shipping-weekday-availability.util';
import { getPickupLocationBackendId } from '../../../utils/pickup-location.util';

@UntilDestroy()
@Component({
  selector: 'app-schedule-order-dialog',
  templateUrl: './schedule-order-dialog.component.html',
  imports: [
    DialogWrapperComponent,
    DatePickerComponent,
    FormsModule,
    TimePickerComponent,
    ButtonComponent,
    ReactiveFormsModule,
    ShippingMethodComponent,
    CheckboxComponent,
    ...DynamicValidationDependencies
  ],
  styleUrl: './schedule-order-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ScheduleOrderDialogComponent
  extends BaseDialogDirective
  implements OnInit, OnDestroy
{
  workingHours: FormattedHours[] = inject(DIALOG_DATA).workingHours;
  rawWorkingHours: WooCommerceRestaurantHoursValue[] =
    inject(DIALOG_DATA).rawWorkingHours || [];
  dateForm!: FormGroup;

  methodDetails: WritableSignal<string> = signal<string>('');
  availableScheduleDates: WritableSignal<Date[]> = signal<Date[]>([]);
  selectedAddress: WritableSignal<DeliveryMethodSelectedAddress> =
    signal<DeliveryMethodSelectedAddress>({} as DeliveryMethodSelectedAddress);
  timeLabel: WritableSignal<string> = signal('Time');

  // Store the fetched settings for pickup and delivery
  private datetimeSettings: WritableSignal<WooCommercePickupLocationDatetimeResponse | null> =
    signal(null);
  isLoading: WritableSignal<boolean> = signal(false);

  // Timer for periodic validation of selected date/time
  private validationIntervalId: ReturnType<typeof setInterval> | null = null;
  private readonly VALIDATION_INTERVAL_MS = 60000; // Check every minute

  constructor(
    private formBuilder: FormBuilder,
    private store: Store,
    private wooCommerceShippingDatetimeApiService: WooCommerceShippingDatetimeApiService,
    private wooCommerceShippingApiService: WooCommerceShippingApiService,
    private toastrService: ToastrService
  ) {
    super();
  }

  protected readonly currentDate: Date = new Date();
  protected readonly ButtonStyle = ButtonStyle;
  protected readonly ButtonSize = ButtonSize;

  /**
   * Get the restaurant's timezone from the store
   */
  private get timezone(): string | undefined {
    return this.store.selectSnapshot(RestaurantState.timezone);
  }

  /**
   * Get current date/time in the restaurant's timezone
   * @returns Object with date components in the restaurant's timezone
   */
  private getNowInTimezone(): {
    year: number;
    month: number;
    day: number;
    hours: number;
    minutes: number;
    totalMinutes: number;
  } {
    const now = new Date();
    const tz = this.timezone;

    if (tz) {
      try {
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: tz,
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          hour12: false
        });
        const parts = formatter.formatToParts(now);
        const year = parseInt(
          parts.find((p) => p.type === 'year')?.value || '0',
          10
        );
        const month =
          parseInt(parts.find((p) => p.type === 'month')?.value || '1', 10) - 1;
        const day = parseInt(
          parts.find((p) => p.type === 'day')?.value || '1',
          10
        );
        const hours = parseInt(
          parts.find((p) => p.type === 'hour')?.value || '0',
          10
        );
        const minutes = parseInt(
          parts.find((p) => p.type === 'minute')?.value || '0',
          10
        );

        return {
          year,
          month,
          day,
          hours,
          minutes,
          totalMinutes: hours * 60 + minutes
        };
      } catch (e) {
        console.warn('Failed to parse timezone, using local time:', e);
      }
    }

    // Fallback to local time
    return {
      year: now.getFullYear(),
      month: now.getMonth(),
      day: now.getDate(),
      hours: now.getHours(),
      minutes: now.getMinutes(),
      totalMinutes: now.getHours() * 60 + now.getMinutes()
    };
  }

  ngOnInit() {
    this.initForm();
    this.startValidationTimer();
  }

  ngOnDestroy() {
    this.stopValidationTimer();
  }

  /**
   * Start periodic validation to detect stale selections
   * This handles the case when user selects a time, leaves for a while,
   * and comes back - their selection might now be in the past
   */
  private startValidationTimer(): void {
    this.validationIntervalId = setInterval(() => {
      this.validateCurrentSelection();
    }, this.VALIDATION_INTERVAL_MS);
  }

  private stopValidationTimer(): void {
    if (this.validationIntervalId) {
      clearInterval(this.validationIntervalId);
      this.validationIntervalId = null;
    }
  }

  /**
   * Validate the current selection and reset if it's now in the past
   */
  private validateCurrentSelection(): void {
    const formValues = this.dateForm.value;
    const selectedDate = formValues['date'];
    const selectedTime = formValues['time'];
    const isAsap = formValues['is_asap'];

    // If ASAP is selected, no need to validate
    if (isAsap) {
      return;
    }

    // If no date selected, nothing to validate
    if (!selectedDate) {
      return;
    }

    const nowInTz = this.getNowInTimezone();

    // Check if the selected date is now in the past
    if (this.isDateInPastWithTimezone(selectedDate, nowInTz)) {
      // Reset the date and time selection
      this.dateForm.patchValue({ date: null, time: null });
      this.dateForm.get('time')?.disable();

      // Re-filter available dates to remove past dates
      this.refreshAvailableDates();

      // Notify user
      this.toastrService.warning(
        'The selected date has passed. Please choose a new date.',
        'Date Expired'
      );
      return;
    }

    // If the date is today, check if the time is still valid
    if (this.isSameDayWithTimezone(selectedDate, nowInTz) && selectedTime) {
      if (this.isTimeInPastWithTimezone(selectedTime, nowInTz)) {
        // Reset only the time selection
        this.dateForm.patchValue({ time: null });

        // Re-trigger date change to update available time slots
        this.onDateChange(selectedDate);

        // Notify user
        this.toastrService.warning(
          'The selected time has passed. Please choose a new time.',
          'Time Expired'
        );
      }
    }
  }

  /**
   * Refresh available dates by re-filtering past dates
   */
  private refreshAvailableDates(): void {
    const selectedAddress = this.selectedAddress();
    if (selectedAddress?.address?.id) {
      this.updateScheduleOptions(selectedAddress);
    }
  }

  onSubmit() {
    if (this.dateForm.invalid) {
      this.dateForm.markAsTouched();
      this.dateForm.markAsDirty();
      return;
    }

    const formValues = this.dateForm.value;
    const selectedDate = formValues['date'];
    const selectedTime = formValues['time'];
    const isAsap = formValues['is_asap'];

    // Validate that the selected date/time is not in the past (unless ASAP)
    if (!isAsap) {
      const nowInTz = this.getNowInTimezone();

      // Check if date is in the past
      if (this.isDateInPastWithTimezone(selectedDate, nowInTz)) {
        // Reset form and refresh available dates
        this.dateForm.patchValue({ date: null, time: null });
        this.refreshAvailableDates();
        this.toastrService.error(
          'The selected date has passed. Please choose a valid date.',
          'Invalid Date'
        );
        return;
      }

      // If today, check if time is in the past
      if (this.isSameDayWithTimezone(selectedDate, nowInTz) && selectedTime) {
        if (this.isTimeInPastWithTimezone(selectedTime, nowInTz)) {
          // Reset time and re-trigger date change to refresh time slots
          this.dateForm.patchValue({ time: null });
          this.onDateChange(selectedDate);
          this.toastrService.error(
            'The selected time has passed. Please choose a valid time.',
            'Invalid Time'
          );
          return;
        }
      }
    }

    const timeSlot = isAsap
      ? WooCommerceDatetimeTimeSlotValue.Asap
      : selectedTime;

    const timestamp = this.calculateTimeslotTimestamp(
      selectedDate,
      timeSlot
    );

    this.closeDialog({
      address: this.selectedAddress().address,
      method: this.selectedAddress().method,
      methodDetails: this.methodDetails(),
      schedule: {
        date: selectedDate,
        time: timeSlot,
        timestamp: timestamp
      }
    });
  }

  /**
   * Calculate timeslot timestamp (date + time) in Unix seconds
   * Uses local date components and treats them as UTC to avoid timezone conversion issues
   * This ensures the timestamp is the same regardless of user's timezone
   * @param date The selected date
   * @param time The selected time (Date object with time only, or time slot string)
   * @returns Unix timestamp in seconds
   */
  private calculateTimeslotTimestamp(date: Date, time: Date | string): number {
    // Get date components using local methods (as selected by user)
    // We'll treat these as UTC to avoid timezone conversion
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    let hour24 = 0;
    let minutes = 0;

    if (time instanceof Date) {
      // If time is a Date object (from time picker), extract hours/minutes
      hour24 = time.getHours();
      minutes = time.getMinutes();
    } else if (typeof time === 'string') {
      // If time is a string (time slot like "09:00 AM - 10:00 AM"), parse the start time
      const timeSlot = time.trim();

      // Extract start time from slot (e.g., "09:00 AM - 10:00 AM" -> "09:00 AM")
      const timeMatch = timeSlot.match(/^(\d{1,2}:\d{2})\s*(AM|PM)/i);
      if (timeMatch) {
        const [, timeStr, period] = timeMatch;
        const [hours, mins] = timeStr.split(':').map(Number);

        hour24 = hours;
        if (period.toUpperCase() === 'PM' && hours !== 12) {
          hour24 += 12;
        } else if (period.toUpperCase() === 'AM' && hours === 12) {
          hour24 = 0;
        }
        minutes = mins;
      }
    }

    // Create date using Date.UTC, treating the local components as UTC
    // This ensures the timestamp is the same regardless of user's timezone
    const dateTime = new Date(
      Date.UTC(year, month, day, hour24, minutes, 0, 0)
    );

    return Math.floor(dateTime.getTime() / 1000);
  }

  onDateChange(
    selectedDate: Date,
    selectedAddress: DeliveryMethodSelectedAddress = this.selectedAddress()
  ) {
    let timeSlots: string[] = this.getAvailableScheduleTimeSlots(
      selectedAddress.address.id,
      selectedAddress.method,
      selectedDate
    );

    // Filter out past time slots if the selected date is today (using timezone)
    const nowInTz = this.getNowInTimezone();
    if (this.isSameDayWithTimezone(selectedDate, nowInTz)) {
      timeSlots = this.filterPastTimeSlotsWithTimezone(timeSlots, nowInTz);
    }

    console.log(timeSlots);

    // Look up working hours for the selected date's weekday
    const workingHoursRange = getWorkingHoursForDate(
      selectedDate,
      this.rawWorkingHours
    );
    const hasWorkingHours = !!workingHoursRange;

    // Check availability conditions
    const hasTimeSlots = timeSlots.length > 0;
    const isOnlyAsapAvailable =
      timeSlots.length === 1 &&
      timeSlots[0].toLowerCase() === WooCommerceDatetimeTimeSlotValue.Asap;
    const hasNoTimeSlots = timeSlots.length === 0;

    this.dateForm.get('time')!.enable();
    this.dateForm.get('time')!.setValue(null);
    this.dateForm.get('time')!.clearValidators();

    if (hasTimeSlots && !isOnlyAsapAvailable) {
      // Slot Mode (Standard behavior) - time_slots have real slots
      const validSlots = timeSlots.filter(
        (slot) => slot.toLowerCase() !== WooCommerceDatetimeTimeSlotValue.Asap
      );

      if (validSlots.length > 0) {
        const firstSlot = validSlots[0];
        const lastSlot = validSlots[validSlots.length - 1];
        this.timeLabel.set(`Time (${firstSlot} - ${lastSlot})`);
      } else {
        this.timeLabel.set('Time');
      }

      this.dateForm
        .get('time')!
        .setValidators([
          Validators.required,
          TimeValidators.isTimeInRange(validSlots)
        ]);
    } else if (hasWorkingHours) {
      // Range Mode - use per-weekday working hours from get_site_info
      this.timeLabel.set(`Time (${workingHoursRange!.range})`);

      this.dateForm
        .get('time')!
        .setValidators([
          Validators.required,
          TimeValidators.isTimeInRange([workingHoursRange!.range])
        ]);
    } else if (isOnlyAsapAvailable || hasNoTimeSlots) {
      // Only ASAP or No Slots and no working hours
      this.dateForm.get('time')!.disable();
    } else {
      // Fallback
      this.dateForm.get('time')!.setValidators([Validators.required]);
    }

    // Enable/Disable ASAP Field
    // ASAP is available if:
    // 1. Only ASAP is in time_slots, OR
    // 2. No time slots and no working hours (pure ASAP mode), OR
    // 3. ASAP is explicitly in the time_slots array
    const isAsapInSlots = timeSlots.some((timeSlot: string) => {
      return timeSlot.toLowerCase() === WooCommerceDatetimeTimeSlotValue.Asap;
    });

    const isAsapAvailable =
      isOnlyAsapAvailable ||
      (hasNoTimeSlots && !hasWorkingHours) ||
      isAsapInSlots;

    if (isAsapAvailable) {
      this.dateForm.get('is_asap')!.enable();

      // Auto-check and disable ASAP if it's the only option
      if ((isOnlyAsapAvailable || hasNoTimeSlots) && !hasWorkingHours) {
        this.dateForm.get('is_asap')!.setValue(true);
        this.dateForm.get('is_asap')!.setValidators([Validators.required]);
        this.dateForm.get('is_asap')!.disable();
      } else {
        this.dateForm.get('is_asap')!.setValue(false);
        this.dateForm.get('is_asap')!.clearValidators();
      }
    } else {
      this.dateForm.get('is_asap')!.disable();
      this.dateForm.get('is_asap')!.setValue(false);
      this.dateForm.get('is_asap')!.clearValidators();
    }
  }

  onIsAsapChange(isAsap: boolean) {
    this.dateForm.get('time')!.enable();

    if (isAsap) {
      this.dateForm.get('time')!.disable();
      this.dateForm.get('time')!.removeValidators(Validators.required);
    } else {
      this.dateForm.get('time')!.enable();
      this.dateForm.get('time')!.addValidators(Validators.required);
    }
  }

  onDeliveryMethodChange(selectedAddress: DeliveryMethodSelectedAddress): void {
    if (selectedAddress.method === ShippingMethod.Delivery) {
      this.store.dispatch(
        new AddDeliveryAddress(
          selectedAddress.address as WooCommerceDeliveryAddress
        )
      );

      // Fetch data for delivery (same as pickup)
      this.isLoading.set(true);
      const deliveryAddress =
        selectedAddress.address as WooCommerceDeliveryAddress;
      this.wooCommerceShippingApiService
        .getShippingMethods({
          state: deliveryAddress.state,
          country: deliveryAddress.country,
          postcode: deliveryAddress.postcode,
          address: deliveryAddress.address_1 || deliveryAddress.address || '',
          city: deliveryAddress.city || ''
        })
        .pipe(
          finalize(() => this.isLoading.set(false)),
          untilDestroyed(this)
        )
        .subscribe({
          next: (response) => {
            // Find the shipping method with datetime_settings (e.g. uber_shipping)
            const shippingMethod = findNotEmptyField(
              'datetime_settings',
              response.shipping_methods
            );

            if (shippingMethod && shippingMethod.datetime_settings) {
              this.datetimeSettings.set({
                delivery_dates:
                  shippingMethod.datetime_settings.delivery_dates || [],
                time_settings: shippingMethod.datetime_settings.time_settings
              });
            } else {
              this.datetimeSettings.set(null);
            }
            this.updateScheduleOptions(selectedAddress);
          },
          error: () => {
            this.datetimeSettings.set(null);
            this.availableScheduleDates.set([]);
            this.dateForm.get('date')?.disable();
          }
        });
    } else if (selectedAddress.method === ShippingMethod.Pickup) {
      // Fetch data for pickup
      this.isLoading.set(true);
      const pickupAddress = selectedAddress.address as WooCommerceShippingAddress;
      this.wooCommerceShippingDatetimeApiService
        .getShippingDatetime(getPickupLocationBackendId(pickupAddress))
        .pipe(
          finalize(() => this.isLoading.set(false)),
          untilDestroyed(this)
        )
        .subscribe({
          next: (response) => {
            this.datetimeSettings.set(response);
            this.updateScheduleOptions(selectedAddress);
          },
          error: () => {
            // Handle error or clear options
            this.datetimeSettings.set(null);
            this.availableScheduleDates.set([]);
            this.dateForm.get('date')?.disable();
          }
        });
    }
  }

  private updateScheduleOptions(
    selectedAddress: DeliveryMethodSelectedAddress
  ) {
    let availableScheduleDates: Date[] = this.getAvailableScheduleDates(
      selectedAddress.address.id,
      selectedAddress.method
    );

    // Filter out holidays from available dates
    const holidays: WooCommerceRestaurantHolidays = this.store.selectSnapshot(
      RestaurantState.holidays
    );
    const timezone = this.store.selectSnapshot(RestaurantState.timezone);
    availableScheduleDates = filterOutHolidays(
      availableScheduleDates,
      holidays,
      timezone
    );

    // Filter out past dates (using timezone)
    const nowInTz = this.getNowInTimezone();
    availableScheduleDates = this.filterPastDatesWithTimezone(
      availableScheduleDates,
      nowInTz
    );
    availableScheduleDates = filterDatesByWeekdayAvailability(
      availableScheduleDates,
      this.datetimeSettings()?.weekdays,
      timezone
    );

    if ((this.rawWorkingHours || []).length > 0) {
      availableScheduleDates = availableScheduleDates.filter(
        (date) => getWorkingHoursForDate(date, this.rawWorkingHours) != null
      );
    }

    this.availableScheduleDates.set(availableScheduleDates);
    this.selectedAddress.set(selectedAddress);

    // Enable/Disable & Reset
    if (availableScheduleDates.length > 0) {
      this.dateForm.get('date')?.enable();
    } else {
      this.dateForm.get('date')?.disable();
    }

    this.dateForm.get('time')?.disable();
    this.dateForm.get('is_asap')?.disable();
    this.dateForm.get('date')?.setValidators([Validators.required]);
    this.dateForm.patchValue({ date: null, time: null, is_asap: false });
  }

  private getAvailableScheduleDates(
    addressId: string,
    shippingMethod: ShippingMethod
  ): Date[] {
    // Use fetched data for both pickup and delivery
    const response = this.datetimeSettings();
    if (response) {
      return (
        response.delivery_dates?.map((date: any): Date => {
          return convertToDate(date.date);
        }) || []
      );
    }

    // Fallbacks
    if (shippingMethod == ShippingMethod.Delivery) {
      const dateTimeSettings = this.store.selectSnapshot(
        DeliveryState.dateTimeSettings(addressId)
      );
      return (
        dateTimeSettings?.delivery_dates?.map(
          (date: WooCommerceDatetimeDeliveryDate): Date => {
            return convertToDate(date.date);
          }
        ) || []
      );
    }

    if (shippingMethod == ShippingMethod.Pickup) {
      const shippingMethod = this.store.selectSnapshot(
        RestaurantState.shippingMethod(addressId)
      );
      this.methodDetails.set(shippingMethod?.id || '');
      return (
        shippingMethod?.datetime_settings?.delivery_dates?.map(
          (date: WooCommerceDatetimeDeliveryDate): Date => {
            return convertToDate(date.date);
          }
        ) || []
      );
    }

    return [];
  }

  private getAvailableScheduleTimeSlots(
    addressId: string,
    shippingMethod: ShippingMethod,
    date: Date
  ): string[] {
    let rawTimeSlots: WooCommerceDatetimeTimeSlot[] = [];

    // Use fetched data for both
    const response = this.datetimeSettings();
    if (response) {
      const dateString = convertToString(date);
      const deliveryDate = response.delivery_dates?.find(
        (d: any) => d.date === dateString
      );
      rawTimeSlots = deliveryDate?.time_slots || [];
    } else {
      // Fallback
      if (shippingMethod == ShippingMethod.Delivery) {
        rawTimeSlots = this.store.selectSnapshot(
          DeliveryState.scheduleTimeSlots(addressId, date)
        );
      }

      if (shippingMethod == ShippingMethod.Pickup) {
        rawTimeSlots = this.store.selectSnapshot(
          RestaurantState.scheduleTimeSlots(addressId, date)
        );
      }
    }

    return rawTimeSlots.map(
      (rawTimeSlot: WooCommerceDatetimeTimeSlot) =>
        rawTimeSlot.time_slot as string
    );
  }

  private initForm(): void {
    this.dateForm = this.formBuilder.group({
      date: [{ value: null, disabled: true }],
      time: [{ value: null, disabled: true }],
      is_asap: [{ value: false, disabled: true }]
    });
  }

  /**
   * Filter out dates that are in the past (timezone-aware)
   * @param dates Array of dates to filter
   * @param nowInTz Current date/time in restaurant timezone
   * @returns Filtered array of dates
   */
  private filterPastDatesWithTimezone(
    dates: Date[],
    nowInTz: ReturnType<typeof this.getNowInTimezone>
  ): Date[] {
    return dates.filter((date: Date) => {
      const dateYear = date.getFullYear();
      const dateMonth = date.getMonth();
      const dateDay = date.getDate();

      // Compare as date only (ignore time)
      if (dateYear > nowInTz.year) return true;
      if (dateYear < nowInTz.year) return false;
      if (dateMonth > nowInTz.month) return true;
      if (dateMonth < nowInTz.month) return false;
      return dateDay >= nowInTz.day;
    });
  }

  /**
   * Filter out time slots that are in the past (timezone-aware)
   * @param timeSlots Array of time slot strings (e.g., "09:00 AM - 10:00 AM")
   * @param nowInTz Current date/time in restaurant timezone
   * @returns Filtered array of time slots
   */
  private filterPastTimeSlotsWithTimezone(
    timeSlots: string[],
    nowInTz: ReturnType<typeof this.getNowInTimezone>
  ): string[] {
    return timeSlots.filter((slot: string) => {
      // Always keep ASAP option
      if (slot.toLowerCase() === WooCommerceDatetimeTimeSlotValue.Asap) {
        return true;
      }

      const timeRange = parseTimeRange(slot);
      if (!timeRange) {
        // If we can't parse the slot, keep it (might be a special format)
        return true;
      }

      // Keep the slot if its end time is still in the future
      // Convert milliseconds to minutes for comparison
      const endMinutes = timeRange.end.milliseconds / (60 * 1000);
      return endMinutes > nowInTz.totalMinutes;
    });
  }

  /**
   * Check if a date is in the past (timezone-aware)
   * @param date Date to check
   * @param nowInTz Current date/time in restaurant timezone
   * @returns True if the date is in the past
   */
  private isDateInPastWithTimezone(
    date: Date,
    nowInTz: ReturnType<typeof this.getNowInTimezone>
  ): boolean {
    const dateYear = date.getFullYear();
    const dateMonth = date.getMonth();
    const dateDay = date.getDate();

    if (dateYear < nowInTz.year) return true;
    if (dateYear > nowInTz.year) return false;
    if (dateMonth < nowInTz.month) return true;
    if (dateMonth > nowInTz.month) return false;
    return dateDay < nowInTz.day;
  }

  /**
   * Check if a date is the same day as the current date in timezone
   * @param date Date to check
   * @param nowInTz Current date/time in restaurant timezone
   * @returns True if both dates are the same day
   */
  private isSameDayWithTimezone(
    date: Date,
    nowInTz: ReturnType<typeof this.getNowInTimezone>
  ): boolean {
    return (
      date.getFullYear() === nowInTz.year &&
      date.getMonth() === nowInTz.month &&
      date.getDate() === nowInTz.day
    );
  }

  /**
   * Check if a time is in the past (timezone-aware)
   * @param time Time value (Date object or time slot string)
   * @param nowInTz Current date/time in restaurant timezone
   * @returns True if the time is in the past
   */
  private isTimeInPastWithTimezone(
    time: Date | string,
    nowInTz: ReturnType<typeof this.getNowInTimezone>
  ): boolean {
    if (time instanceof Date) {
      const timeMinutes = time.getHours() * 60 + time.getMinutes();
      return timeMinutes <= nowInTz.totalMinutes;
    }

    if (typeof time === 'string') {
      // Handle ASAP - never in the past
      if (time.toLowerCase() === WooCommerceDatetimeTimeSlotValue.Asap) {
        return false;
      }

      // Parse time slot string
      const timeRange = parseTimeRange(time);
      if (timeRange) {
        // Check if the end time is in the past
        const endMinutes = timeRange.end.milliseconds / (60 * 1000);
        return endMinutes <= nowInTz.totalMinutes;
      }
    }

    return false;
  }
}
